import { useEffect, useMemo, useState } from 'react';

import { money, moneyExact, purposeLabel, shortDate } from '../lib/format';
import { apifySpend } from '../lib/scan';
import { supabase } from '../lib/supabase';
import type { ApifySpend, LlmCall } from '../lib/types';

/**
 * Cost telemetry.
 *
 * plan.md section 6.7 requires `llm_calls` to total under £3 after a week of live running, and the
 * only way that stays true is if the number is visible without opening the database.
 *
 * Two changes from the four-decimal tables this used to be:
 *
 *   - Money is shown at a scale a person has. This is a £0-£20/month budget: the useful unit below
 *     a pound is pence, so `£0.0043` - technically exact, readable as nothing - is `0.43p`. The
 *     unrounded figure stays in the tooltip.
 *   - "By day" was a table of seven rows with four decimal places, which answers "what did Tuesday
 *     cost" when the question is "is this trending up". It is a bar row now, and the zero days are
 *     visible as the absence of a bar rather than as seven identical £0.0000 lines.
 *
 * Apify spend used to be "still P2.14". It is here now, and it is measured rather than
 * estimated: the figure comes from Apify's own usage endpoint, through the same narrow
 * bridge that starts a scan, so this page and the Apify console cannot disagree. That
 * matters more than it sounds - the whole point of the number is to decide how big the
 * watchlist can be, and a widget that quietly disagreed with the bill would be worse than
 * no widget.
 */

interface TileProps {
  label: string;
  value: string;
  hint?: string;
  title?: string;
}

function Tile({ label, value, hint, title }: TileProps) {
  return (
    <div className="tile">
      <div className="muted small">{label}</div>
      <div className="value" title={title}>
        {value}
      </div>
      {hint ? <div className="muted small">{hint}</div> : null}
    </div>
  );
}

export default function Spend() {
  const [calls, setCalls] = useState<LlmCall[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  /**
   * Apify is a *monthly cycle*, not a window, so it is fetched once and refreshed on demand
   * rather than being wired to the select above. Conflating the two would suggest the "last
   * 24 hours" option changed it.
   */
  const [apify, setApify] = useState<ApifySpend | null>(null);
  const [apifyError, setApifyError] = useState<string | null>(null);
  const [apifyLoading, setApifyLoading] = useState(true);

  const loadApify = useMemo(
    () => async () => {
      setApifyLoading(true);
      const result = await apifySpend();
      setApifyLoading(false);
      if (!result.ok || !result.data) {
        setApifyError(result.error ?? 'Could not read the Apify figure.');
        return;
      }
      setApifyError(null);
      setApify(result.data);
    },
    [],
  );

  useEffect(() => {
    void loadApify();
  }, [loadApify]);

  useEffect(() => {
    const since = new Date(Date.now() - days * 86_400_000).toISOString();
    setLoading(true);
    void supabase
      .from('llm_calls')
      .select(
        'id, purpose, model, prompt_version, tokens_in, tokens_out, cost_gbp, latency_ms, attempts, ok, created_at',
      )
      .gte('created_at', since)
      .order('created_at', { ascending: false })
      .limit(5000)
      .then(({ data, error: readError }) => {
        if (readError) setError(readError.message);
        else setCalls((data ?? []) as LlmCall[]);
        setLoading(false);
      });
  }, [days]);

  const summary = useMemo(() => {
    const total = calls.reduce((sum, call) => sum + Number(call.cost_gbp ?? 0), 0);
    const tokensIn = calls.reduce((sum, call) => sum + call.tokens_in, 0);
    const tokensOut = calls.reduce((sum, call) => sum + call.tokens_out, 0);
    const failures = calls.filter((call) => !call.ok).length;

    const byPurpose = new Map<string, { calls: number; cost: number; failures: number }>();
    const byDay = new Map<string, number>();

    for (const call of calls) {
      const purpose = byPurpose.get(call.purpose) ?? { calls: 0, cost: 0, failures: 0 };
      purpose.calls += 1;
      purpose.cost += Number(call.cost_gbp ?? 0);
      if (!call.ok) purpose.failures += 1;
      byPurpose.set(call.purpose, purpose);

      const day = call.created_at.slice(0, 10);
      byDay.set(day, (byDay.get(day) ?? 0) + Number(call.cost_gbp ?? 0));
    }

    const days7 = Array.from(byDay.entries())
      .filter(([day]) => new Date(day).getTime() >= Date.now() - 7 * 86_400_000)
      .reduce((sum, [, cost]) => sum + cost, 0);

    return {
      total,
      tokensIn,
      tokensOut,
      failures,
      byPurpose: Array.from(byPurpose.entries()).sort((a, b) => b[1].cost - a[1].cost),
      last7Days: days7,
      byDay,
    };
  }, [calls]);

  /** The last seven calendar days, zeros included, so a quiet day is visibly quiet. */
  const bars = useMemo(() => {
    const midnight = new Date();
    midnight.setHours(0, 0, 0, 0);
    return Array.from({ length: 7 }, (_, index) => {
      const day = new Date(midnight.getTime() - (6 - index) * 86_400_000);
      const key = day.toISOString().slice(0, 10);
      return {
        key,
        label: day.toLocaleDateString('en-GB', { weekday: 'short' }),
        cost: summary.byDay.get(key) ?? 0,
      };
    });
  }, [summary.byDay]);

  const peak = Math.max(...bars.map((bar) => bar.cost), 0);

  return (
    <section>
      <div className="draft-head">
        <div>
          <h2>Spend</h2>
          <p className="muted small">
            Model cost recorded in <code>llm_calls</code>, and the Apify credit — the other half
            of the ledger.
          </p>
        </div>
        <div>
          <label htmlFor="window">Window</label>
          <select id="window" value={days} onChange={(event) => setDays(Number(event.target.value))}>
            <option value={1}>Last 24 hours</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>
      </div>

      {error ? <p className="error">{error}</p> : null}
      {loading ? <p className="muted">Loading…</p> : null}

      <h3>Apify credit</h3>
      <p className="muted small">
        Read from Apify&rsquo;s own usage endpoint, not summed from our runs, so this figure and
        the Apify console cannot disagree. It is a monthly cycle — the window above does not
        apply to it.
      </p>

      {apifyLoading ? <p className="muted">Loading…</p> : null}
      {apifyError ? (
        <p className="error">
          {apifyError}{' '}
          <button type="button" className="link" onClick={() => void loadApify()}>
            Try again
          </button>
        </p>
      ) : null}

      {apify && !apify.configured ? (
        <p className="muted">
          <code>APIFY_TOKEN</code> is not set on the Worker, so there is nothing to read. Scans
          cannot run either — see the scan section of the README.
        </p>
      ) : null}

      {apify?.configured ? (
        <>
          <div className="tiles">
            <Tile
              label="Used this cycle"
              value={apify.usedUsd === null ? 'not reported' : `$${apify.usedUsd.toFixed(4)}`}
              hint={
                apify.creditUsd === null
                  ? apify.plan
                    ? `Plan: ${apify.plan}`
                    : undefined
                  : `of $${apify.creditUsd.toFixed(2)} free credit`
              }
            />
            <Tile
              label="Left"
              value={
                apify.usedUsd === null || apify.creditUsd === null
                  ? '—'
                  : `$${Math.max(apify.creditUsd - apify.usedUsd, 0).toFixed(4)}`
              }
              hint={
                apify.creditUsd === null
                  ? 'no ceiling reported'
                  : 'Over the credit, the free plan blocks until the cycle turns over.'
              }
            />
            <Tile
              label="Cycle ends"
              value={apify.cycleEndAt ? shortDate(apify.cycleEndAt) : '—'}
              hint={apify.username ?? undefined}
            />
          </div>

          {apify.byService.length > 0 ? (
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Service</th>
                    <th className="num">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {apify.byService.map((entry) => (
                    <tr key={entry.service}>
                      <td>{entry.service}</td>
                      <td className="num">${entry.usd.toFixed(4)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          <p className="muted small">
            <button type="button" className="link" onClick={() => void loadApify()}>
              Refresh
            </button>
          </p>
        </>
      ) : null}

      <div className="tiles">
        <Tile
          label="Total cost"
          value={money(summary.total)}
          title={moneyExact(summary.total)}
          hint={`Last ${days} days`}
        />
        <Tile
          label="Last 7 days"
          value={money(summary.last7Days)}
          title={moneyExact(summary.last7Days)}
          hint="Target: under £3/week"
        />
        <Tile label="Calls" value={String(calls.length)} />
        <Tile
          label="Failures"
          value={String(summary.failures)}
          hint={summary.failures > 0 ? 'Not billed twice — see attempts in the table' : undefined}
        />
        <Tile label="Tokens in" value={summary.tokensIn.toLocaleString('en-GB')} />
        <Tile label="Tokens out" value={summary.tokensOut.toLocaleString('en-GB')} />
      </div>

      <h3>Last 7 days</h3>
      <div
        className="bars"
        role="img"
        aria-label={`Cost per day over the last seven days: ${bars
          .map((bar) => `${bar.label} ${money(bar.cost)}`)
          .join(', ')}`}
      >
        {bars.map((bar) => (
          <div key={bar.key} className={bar.cost === 0 ? 'bar zero' : 'bar'}>
            <span
              className="fill"
              title={`${bar.key}: ${moneyExact(bar.cost)}`}
              style={{ height: peak > 0 ? `${Math.max((bar.cost / peak) * 100, 2)}%` : '2%' }}
            />
          </div>
        ))}
      </div>
      <div className="bars-labels" aria-hidden="true">
        {bars.map((bar) => (
          <span className="label" key={bar.key} title={moneyExact(bar.cost)}>
            {bar.label} · {money(bar.cost)}
          </span>
        ))}
      </div>

      <h3>By purpose</h3>
      {summary.byPurpose.length === 0 ? (
        <p className="muted">No calls recorded in this window.</p>
      ) : (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Purpose</th>
                <th className="num">Calls</th>
                <th className="num">Failures</th>
                <th className="num">Cost</th>
              </tr>
            </thead>
            <tbody>
              {summary.byPurpose.map(([purpose, entry]) => (
                <tr key={purpose}>
                  <td>{purposeLabel(purpose)}</td>
                  <td className="num">{entry.calls}</td>
                  <td className={entry.failures > 0 ? 'num warn' : 'num'}>{entry.failures}</td>
                  <td className="num" title={moneyExact(entry.cost)}>
                    {money(entry.cost)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
